import type { Identifier } from "ra-core";

/** An item dropped in a column, shown there until the refetch confirms the move. */
export interface PendingMove<T> {
  item: T;
  columnId: string;
  position: number;
}

/**
 * A paginated column as displayed while drops are being saved: the items moved
 * out are hidden and the items moved in are shown (`moveInto` places them).
 * `countDelta` corrects the column total returned by the server. The result is
 * not sorted.
 */
export const applyPendingMoves = <T extends { id: Identifier }>(
  items: T[],
  pendingMoves: Record<string, PendingMove<T>>,
  columnId: string,
  moveInto: (item: T, position: number) => T,
): { items: T[]; countDelta: number } => {
  const kept = items.flatMap((item) => {
    const move = pendingMoves[String(item.id)];
    if (!move) return [item];
    return move.columnId === columnId ? [moveInto(item, move.position)] : [];
  });
  const keptIds = new Set(kept.map((item) => String(item.id)));
  const movedIn = Object.values(pendingMoves)
    .filter(
      (move) =>
        move.columnId === columnId && !keptIds.has(String(move.item.id)),
    )
    .map((move) => moveInto(move.item, move.position));
  return {
    items: [...kept, ...movedIn],
    countDelta: movedIn.length - (items.length - kept.length),
  };
};
